import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { faStar as faStarSolid, faDownload, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Fade } from 'react-awesome-reveal';
import { fetchDeveloperApps } from '../utils/googlePlayService';
import '../styles/apps.css';

export default function Apps() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadApps = async () => {
            try {
                setLoading(true);
                const fetchedApps = await fetchDeveloperApps();
                setApps(fetchedApps);
                setError(null);
            } catch (err) {
                console.error('Failed to load apps:', err);
                setError('Failed to load apps. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        loadApps();
    }, []);

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        for (let i = 0; i < 5; i++) {
            stars.push(
                <FontAwesomeIcon
                    key={i}
                    icon={faStarSolid}
                    className={`star ${i < fullStars ? 'star--filled' : 'star--empty'}`}
                />
            );
        }
        return stars;
    };

    return (
        <main className="apps-page">
            <div className="apps-page__orb apps-page__orb--1" />
            <div className="apps-page__orb apps-page__orb--2" />

            <Fade direction="up" triggerOnce cascade damping={0.08}>
                <div className="apps-header">
                    <span className="apps-label">Our Collection</span>
                    <h1>Android Apps</h1>
                    <p className="apps-subtitle">
                        Discover our apps on the Google Play Store
                    </p>
                    <a
                        href='https://play.google.com/store/apps/dev?id=6256207236238699098'
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-all-button"
                    >
                        <FontAwesomeIcon icon={faGooglePlay} />
                        <span>View All on Google Play</span>
                    </a>
                </div>
            </Fade>

            {loading && (
                <div className="loading-container">
                    <div className="loading-spinner" />
                    <p>Fetching apps...</p>
                </div>
            )}

            {error && (
                <div className="error-container">
                    <p>{error}</p>
                </div>
            )}

            {!loading && !error && apps.length > 0 && (
                <div className="apps-list">
                    {apps.map((app, index) => (
                        <Fade key={app.appId || index} direction="up" delay={index * 60} triggerOnce>
                            <div className="app-card">
                                {/* Top row: icon + info + CTA */}
                                <div className="app-card__top">
                                    <span className="app-card__index">
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <img
                                        src={app.icon}
                                        alt={app.title}
                                        className="app-card__icon"
                                        referrerPolicy="no-referrer"
                                    />
                                    <div className="app-card__info">
                                        <h3 className="app-card__title">{app.title}</h3>
                                        <p className="app-card__summary">{app.summary}</p>
                                        <div className="app-card__meta">
                                            {(Number(app.scoreText || app.score) > 0) ? (
                                                <div className="app-card__stars" aria-label={`${app.scoreText || app.score} out of 5 stars`}>
                                                    {renderStars(app.scoreText || app.score)}
                                                    <span className="app-card__rating">
                                                        {app.scoreText || app.score?.toFixed(1)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="app-card__new">New</span>
                                            )}
                                            <span className="app-card__divider" />
                                            <span className="app-card__stat">
                                                <FontAwesomeIcon icon={faDownload} />
                                                {app.installs || 'N/A'}
                                            </span>
                                            <span className="app-card__badge">
                                                {app.free ? 'Free' : `$${app.price}`}
                                            </span>
                                        </div>
                                    </div>
                                    <a
                                        href={app.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="app-card__cta"
                                    >
                                        <FontAwesomeIcon icon={faGooglePlay} />
                                        <span>Google Play</span>
                                        <FontAwesomeIcon icon={faArrowRight} className="app-card__cta-arrow" />
                                    </a>
                                </div>

                                {/* Screenshot gallery */}
                                {app.screenshots && app.screenshots.length > 1 && (
                                    <div className="app-card__gallery-wrap">
                                        <span className="app-card__gallery-label">Drag to explore</span>
                                        <div className="app-card__gallery" tabIndex="0" aria-label={`${app.title} screenshots`}>
                                            {app.screenshots.slice(1).map((s, idx) => (
                                                <img
                                                    key={idx}
                                                    src={s}
                                                    alt={`${app.title} screenshot ${idx + 1}`}
                                                    className="app-card__screen"
                                                    referrerPolicy="no-referrer"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Fade>
                    ))}
                </div>
            )}

            {!loading && !error && apps.length === 0 && (
                <div className="empty-container">
                    <p>No apps found.</p>
                </div>
            )}
        </main>
    );
}
